import { useApolloClient } from '@apollo/client';
import { DatePicker, Dialog, Label, Loader, Position, Theme, Toast } from '@busybusy/webapp-react-ui';
import {
  EquipmentCategoryCreateInput,
  EquipmentMakeCreateInput,
  EquipmentModelCreateInput,
  OrganizationCustomFields,
  OrganizationCustomFieldsTypes,
} from '__generated__/graphql';
import { CREATE_EQUIPMENT, UPDATE_EQUIPMENT } from 'apollo/mutations/equipment-mutations';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import { equipmentCategoriesWithTypeQuery } from 'components/domain/equipment/EquipmentCategoriesForTypePicker/equipment-cateory-picker-queries';
import { equipmentMakesQuery } from 'components/domain/equipment/EquipmentMakesForCategoryPicker/equipment-makes-picker-queries';
import { equipmentModelsQuery } from 'components/domain/equipment/EquipmentModelsForMakePicker/equipment-models-picker-queries';
import useEquipmentCategories from 'containers/manage-euipment/hooks/useEquipmentCategories';
import useEquipmentMakes from 'containers/manage-euipment/hooks/useEquipmentMakes';
import useEquipmentModels from 'containers/manage-euipment/hooks/useEquipmentModels';
import { useActiveMember, useApolloPaging, useEquipmentHours, useOpenable, useOrganization } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import useCreateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useCreateEquipmentCostHistory';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import {
  camelCase,
  cloneDeep,
  first,
  isEmpty,
  isNil,
  isNull,
  keyBy,
  map,
  mapKeys,
  sortBy,
  toLower,
  toNumber,
} from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import ICsvParseResult from 'types/CsvParseResult';
import IEquipment from 'types/Equipment';
import IEquipmentCategory from 'types/EquipmentCategory';
import IEquipmentMake from 'types/EquipmentMake';
import IEquipmentModel from 'types/EquipmentModel';
import { dateUtils } from 'utils';
import { EQUIPMENT_CSV_HEADERS } from 'utils/constants/csvConstants';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { getCurrentEquipmentCostHistory, StringToEquipmentModelType } from 'utils/equipmentUtils';
import fileUtils from 'utils/fileUtils';
import { t } from 'utils/localize';
import { isNumberString } from 'utils/numberUtils';
import { CFIndex, CFTitleKey, getCustomFieldTitles, MAX_CUSTOM_FIELDS } from 'utils/organizationCustomFieldsUtils';
import { isAdmin } from 'utils/positionUtils';
import { convertEmptyToNull, csvEscape } from 'utils/stringUtils';
import { uuid } from 'utils/uuidUtils';
import CsvImportDialog from '../../csv-import/CsvImportDialog/CsvImportDialog';
import CsvImportOverview from '../../csv-import/CsvImportOverview/CsvImportOverview';
import CsvImportReview from '../../csv-import/CsvImportReview/CsvImportReview';
import ReimportContent from '../../csv-import/ReimportContent/ReimportContent';
import ICSVEquipment, {
  equipmentImportErrorMap,
  IEquipmentCsvImportErrorState,
} from '../../csv-import/types/ImportEquipmentTypes';
import {
  getCsvIdErrors,
  getErrorMessages,
  parseCsvForReimport,
  parseCsvWithDefaults,
  validateDataStructure,
  validateHeaders,
} from '../../csv-import/utils/utils';
import CSVImportEquipmentOverviewTable from './CSVImportEquipmentOverviewTable/CSVImportEquipmentOverviewTable';
import CSVImportEquipmentReviewTable from './CSVImportEquipmentReviewTable/CSVImportEquipmentReviewTable';
import useEquipmentCSVValidation from './hooks/useEquipmentCSVValidation';
import {
  convertCsvEquipmentToEquipment,
  convertCsvEquipmentToUpdateEquipment,
  getEquipmentCsvHeaders,
} from './utils/utils';

export interface IEquipmentCategoryCSV {
  category: string;
}

export interface IImportedEquipment {
  name: string;
  category: string;
  model: string;
  make: string;
  year: string;
  hourMeter: string;
  modelId: string;
  trackHourMeter: boolean;
  success: boolean;
  type: string;
  customField1?: string | null;
  customField2?: string | null;
  customField3?: string | null;
  customField4?: string | null;
  customField5?: string | null;
}

export interface ICSVImportEquipmentContainerProps {
  className?: ClassName;
  importDialogOpen: boolean;
  onImportDialogClose: () => void;
  onImportComplete: () => void;
  onReimportDialogClose: () => void;
  reimportDialogOpen: boolean;
  onDownloadList: () => void;
}

const CSVImportEquipmentContainer = (props: ICSVImportEquipmentContainerProps) => {
  const {
    className,
    importDialogOpen,
    onImportDialogClose,
    onImportComplete,
    onReimportDialogClose,
    reimportDialogOpen,
    onDownloadList,
  } = props;

  const loaderDetails = useOpenable();
  const [loaderMessage, setLoaderMessage] = useState<string>();
  const toastDetails = useOpenable();
  const [toastErrorMessage, setToastErrorMessage] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [errors, setErrors] = useState<IEquipmentCsvImportErrorState[]>([]);
  const [prevalidationErrors, setPrevalidationErrors] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<ICSVEquipment[]>([]);
  const [importedEquipment, setImportedEquipment] = useState<IImportedEquipment[]>([]);
  const equipmentOverviewDialogDetails = useOpenable({ onClose: onDialogClose });
  const equipmentReviewDialogDetails = useOpenable({ onClose: onDialogClose });
  const overviewRef = useRef<HTMLElement>();
  const reviewRef = useRef<HTMLElement>();
  const validate = useEquipmentCSVValidation();
  const client = useApolloClient();
  const { createEquipmentCategory } = useEquipmentCategories();
  const { createEquipmentMake } = useEquipmentMakes();
  const { createEquipmentModel } = useEquipmentModels();
  const { getAll } = useApolloPaging();
  const organization = useOrganization();
  const { createEquipmentHours } = useEquipmentHours();
  const [isReimport, setIsReimport] = useState<boolean>(false);
  const createEquipmentCostHistory = useCreateEquipmentCostHistory();
  const datePickerOpenable = useOpenable();
  const [costHistoryEffectiveDate, setCostHistoryEffectiveDate] = useState(DateTime.local().startOf('day').toISO());
  const brand = useBrandTitle();
  const { getOrganizationCustomFields } = useOrganizationCustomFields();
  const activeMember = useActiveMember();

  const [organizationCustomFields, setOrganizationCustomFields] = useState<OrganizationCustomFields | null>(null);
  const [CSVHeaders, setCSVHeaders] = useState<string>(EQUIPMENT_CSV_HEADERS);

  useEffect(() => {
    fetchOrganizationCustomFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrganizationCustomFields = async () => {
    const orgCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);
    setOrganizationCustomFields(orgCustomFields ?? null);

    const customFieldTitles = getCustomFieldTitles(isAdmin(activeMember.position), orgCustomFields).map((title) =>
      csvEscape(title)
    );
    setCSVHeaders(getEquipmentCsvHeaders().split(',').concat(customFieldTitles).join(','));
  };

  async function getCategory(categoryTitle: string) {
    if (!isNil(convertEmptyToNull(categoryTitle))) {
      try {
        const results = await client.query<{ equipmentCategories: IEquipmentCategory[] }>({
          query: equipmentCategoriesWithTypeQuery,
          fetchPolicy: 'network-only',
          variables: {
            first: 1,
            filter: {
              organizationId: { equal: organization.id },
              title: { contains: categoryTitle },
              deletedOn: { isNull: true },
            },
          },
        });
        return first(results.data.equipmentCategories);
      } catch (error) {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  async function getMake(makeTitle: string) {
    if (!isNil(convertEmptyToNull(makeTitle))) {
      try {
        const results = await client.query<{ equipmentMakes: IEquipmentMake[] }>({
          query: equipmentMakesQuery,
          fetchPolicy: 'network-only',
          variables: {
            first: 1,
            filter: {
              organizationId: { equal: organization.id },
              title: { contains: makeTitle },
              deletedOn: { isNull: true },
            },
          },
        });
        return first(results.data.equipmentMakes);
      } catch (error) {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  async function getModel(modelTitle: string, makeId: string, categoryId: string, equipmentModelType: string) {
    if (!isNil(convertEmptyToNull(modelTitle))) {
      try {
        const results = await client.query<{ equipmentModels: IEquipmentModel[] }>({
          query: equipmentModelsQuery,
          fetchPolicy: 'network-only',
          variables: {
            filter: {
              equipmentCategoryId: { equal: categoryId },
              equipmentMakeId: { equal: makeId },
              title: { contains: modelTitle },
              type: { equal: equipmentModelType },
              deletedOn: { isNull: true },
              organizationId: { equal: organization.id },
            },
          },
        });
        return first(results.data.equipmentModels);
      } catch (error) {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  useEffect(() => {
    if (printing) {
      window.print();
      setPrinting(false);
    }
  }, [printing]);

  function onDialogClose() {
    setTimeout(() => {
      setEquipment([]);
      setErrors([]);
    }, 250);
  }

  function closeImportDialog() {
    setErrors([]);
    setPrevalidationErrors([]);
    setEquipment([]);
    onDialogClose();
    onImportDialogClose();
    onReimportDialogClose();
  }

  function openErrorToastWithMessage(message: string) {
    setToastErrorMessage(message);
    toastDetails.open();
  }

  function openLoaderDetailsWithMessage(message: string) {
    setLoaderMessage(message);
    loaderDetails.open();
  }

  function clearData() {
    setEquipment([]);
    setErrors([]);
    setPrevalidationErrors([]);
  }

  function parseCsv(reimport: boolean) {
    return async (file: File) => {
      clearData();

      const csvParseFn = reimport ? parseCsvForReimport : parseCsvWithDefaults;

      try {
        if (file.name.indexOf('.csv') !== -1) {
          openLoaderDetailsWithMessage(t('Parsing...'));
          csvParseFn(file, onCsvParseComplete(reimport));
        } else if (file.name.indexOf('.xlsx') !== -1) {
          openLoaderDetailsWithMessage(t('Parsing...'));
          csvParseFn(await fileUtils.convertXlsxToCsv(file), onCsvParseComplete(reimport));
        } else {
          openErrorToastWithMessage(t('Invalid file format. Expected either csv or xlsx.'));
        }
      } catch (e) {
        openErrorToastWithMessage(t('Invalid file format. Expected either csv or xlsx.'));
      }
    };
  }

  function onCsvParseComplete(reimport: boolean) {
    setIsReimport(reimport);
    return reimport ? csvReimport : csvImport;
  }

  async function csvImport(parseResult: ICsvParseResult<any>) {
    openLoaderDetailsWithMessage(t('Validating...'));

    const validStructure = validateStructure(parseResult, false);
    if (!validStructure) {
      loaderDetails.close();
      return;
    }

    // Cast is safe since it now has an id.
    const csvEquipment = convertParseResult(parseResult, organizationCustomFields).map((csvEquipment) => ({
      id: uuid(),
      ...csvEquipment,
      reimport: false,
    })) as ICSVEquipment[];

    const validData = await validateData(csvEquipment, false);

    if (!validData) {
      loaderDetails.close();
      return;
    }

    setLoaderMessage('Populating Data...');
    await populateData(csvEquipment);
    loaderDetails.close();
  }

  async function csvReimport(parseResult: ICsvParseResult<any>) {
    const reimport = true;
    setLoaderMessage(t('Validating...'));

    const validStructure = validateStructure(parseResult, true);
    if (!validStructure) {
      loaderDetails.close();
      return;
    }

    const csvEquipment = convertParseResult(parseResult, organizationCustomFields).map((csvEquipment) => ({
      ...csvEquipment,
      reimport,
    })) as ICSVEquipment[];

    const errs: IEquipmentCsvImportErrorState[] = getCsvIdErrors(csvEquipment);
    if (!isEmpty(errs)) {
      setErrors(errs);
      loaderDetails.close();
      return null;
    }

    // This needs to happen before the validation so we have all the columns populated for sure.
    const populatedEquipment = await populateReimportData(csvEquipment);

    if (isNull(populatedEquipment)) {
      loaderDetails.close();
      return;
    }

    const validData = await validateData(populatedEquipment ?? [], reimport);

    if (!validData) {
      loaderDetails.close();
      return;
    }

    onReimportDialogClose();
    setErrors([]);
    setPrevalidationErrors([]);

    equipmentOverviewDialogDetails.open();
    setTimeout(() => {
      // Wait for dialog refs to get set
      setEquipment(sortBy(populatedEquipment, (e) => toLower(e.name)));
    }, 0);

    loaderDetails.close();
  }

  function convertParseResult(
    parseResult: ICsvParseResult<any>,
    orgCustomFields?: OrganizationCustomFields | null
  ): Array<Partial<ICSVEquipment>> {
    return map(parseResult.data, (result) => {
      const normalized = mapKeys(result, (_value, key) => camelCase(toLower(key)));
      const out: any = { ...normalized };

      if (!isNil(orgCustomFields)) {
        for (let i = 1 as CFIndex; i <= MAX_CUSTOM_FIELDS; i = (i + 1) as CFIndex) {
          const titleKey = `title${i}` as CFTitleKey;
          const header = orgCustomFields[titleKey];

          if (typeof header === 'string' && header.trim().length > 0) {
            const normalizedHeaderKey = camelCase(toLower(header));
            out[`customField${i}`] = normalized[normalizedHeaderKey] ?? null;
            delete out[normalizedHeaderKey];
          }
        }
      }

      return out as ICSVEquipment;
    });
  }

  function validateStructure(parseResult: ICsvParseResult<any>, reimport: boolean) {
    const structureError = validateDataStructure(parseResult);

    if (structureError) {
      setPrevalidationErrors([structureError]);
      return false;
    }

    const headers = parseResult.meta.fields?.map((field) => (field === 'id' ? 'DATA KEY' : field));

    const equipmentHeaders = CSVHeaders.split(',');
    const allowedHeaders = reimport ? equipmentHeaders.concat('DATA KEY') : equipmentHeaders;
    const requiredHeaders = reimport ? ['DATA KEY'] : ['Name'];

    const headerErrs = validateHeaders(headers ?? [], allowedHeaders, requiredHeaders);

    if (headerErrs.length !== 0) {
      setPrevalidationErrors(headerErrs);
      return false;
    } else if (parseResult.data.length === 0) {
      setErrors([{ type: 'EMPTY_CSV', lineNumber: null }]);
      return false;
    } else {
      return true;
    }
  }

  async function validateData(csvEquipment: Array<Partial<ICSVEquipment>>, reimport: boolean) {
    const validations = await validate(csvEquipment, reimport);
    if (validations.length > 0) {
      setErrors(validations);
      return false;
    } else {
      return true;
    }
  }

  async function populateData(csvEquipment: ICSVEquipment[]) {
    const equipmentValues = csvEquipment.map(async (equipment) => {
      const temp = { ...equipment };

      temp.type = temp.type?.trim().toLowerCase();

      if (temp.category) {
        const fetchedCategory = await getCategory(temp.category);
        if (fetchedCategory) {
          temp.categoryId = fetchedCategory.id;
          temp.category = fetchedCategory.title;
        }
      }

      if (temp.make) {
        const fetchedMake = await getMake(temp.make);
        if (fetchedMake) {
          temp.makeId = fetchedMake.id;
          temp.make = fetchedMake.title;
        }
      }

      if (temp.model && temp.makeId && temp.categoryId && temp.type) {
        const fetchedModel = await getModel(temp.model, temp.makeId, temp.categoryId, temp.type);
        if (fetchedModel) {
          temp.modelId = fetchedModel.id;
          temp.model = fetchedModel.title;
        }
      }

      return temp;
    });

    Promise.all(equipmentValues).then((values) => {
      // Wait for dialog ref
      setTimeout(() => {
        setEquipment(sortBy(values, (e) => toLower(e.name)));
      }, 0);

      loaderDetails.close();
      onImportDialogClose();
      onReimportDialogClose();
      setErrors([]);

      equipmentOverviewDialogDetails.open();
    });
  }

  async function populateReimportData(csvEquipment: ICSVEquipment[]) {
    const grouped = keyBy(csvEquipment, 'id');

    try {
      const currentEquipment = await getAll<IEquipment>('equipment', {
        fetchPolicy: 'network-only',
        query: EQUIPMENT_QUERY,
        variables: {
          filter: {
            id: { contains: Object.keys(grouped) },
          },
        },
      });

      const queriedEquipmentMap = keyBy(currentEquipment, 'id');
      if (currentEquipment) {
        const promises = csvEquipment.map(async (equipment) => {
          // This is not necessarily safe yet! This has not validated that the equipment exists for this id yet! Rethink! TODO
          const queried = queriedEquipmentMap[equipment.id];
          const combinedEquipment = cloneDeep(equipment);

          if (isNil(combinedEquipment.name)) {
            combinedEquipment.name = queried.equipmentName;
          }

          combinedEquipment.type = combinedEquipment.type?.trim().toLowerCase();

          if (isNil(combinedEquipment.category)) {
            combinedEquipment.category = queried.model.category.title;
            combinedEquipment.categoryId = queried.model.category.id;
          }

          if (isNil(combinedEquipment.make)) {
            combinedEquipment.make = queried.model.make.title;
            combinedEquipment.makeId = queried.model.make.id;
          }

          if (isNil(combinedEquipment.model)) {
            combinedEquipment.model = queried.model.title;
            combinedEquipment.modelId = queried.model.id;
          }

          if (isNil(combinedEquipment.year)) {
            combinedEquipment.year = queried.year?.toString();
          }

          const fetchedCategory = await getCategory(combinedEquipment.category);
          if (fetchedCategory) {
            combinedEquipment.categoryId = fetchedCategory.id;
            combinedEquipment.category = fetchedCategory.title;
          }

          const fetchedMake = await getMake(combinedEquipment.make);
          if (fetchedMake) {
            combinedEquipment.makeId = fetchedMake.id;
            combinedEquipment.make = fetchedMake.title;
          }

          if (combinedEquipment.makeId && combinedEquipment.categoryId && combinedEquipment.type) {
            const fetchedModel = await getModel(
              combinedEquipment.model,
              combinedEquipment.makeId,
              combinedEquipment.categoryId,
              combinedEquipment.type
            );
            if (fetchedModel) {
              combinedEquipment.modelId = fetchedModel.id;
              combinedEquipment.model = fetchedModel.title;
            }
          }

          if (isNil(combinedEquipment.hourMeter) && !isNil(queried.lastHours?.runningHours)) {
            combinedEquipment.hourMeter = queried.lastHours!.runningHours.toFixed(1);
          }

          if (
            isNil(combinedEquipment.hourlyRate) &&
            !isNil(getCurrentEquipmentCostHistory(queried.costHistory ?? [])?.operatorCostRate)
          ) {
            combinedEquipment.hourlyRate = (
              getCurrentEquipmentCostHistory(queried.costHistory ?? [])?.operatorCostRate ?? 0
            ).toFixed(2);
          }

          if (isNil(combinedEquipment.customField1)) {
            combinedEquipment.customField1 = queried.customField1;
          }

          if (isNil(combinedEquipment.customField2)) {
            combinedEquipment.customField2 = queried.customField2;
          }

          if (isNil(combinedEquipment.customField3)) {
            combinedEquipment.customField3 = queried.customField3;
          }

          if (isNil(combinedEquipment.customField4)) {
            combinedEquipment.customField4 = queried.customField4;
          }

          if (isNil(combinedEquipment.customField5)) {
            combinedEquipment.customField5 = queried.customField5;
          }

          return combinedEquipment;
        });
        return Promise.all(promises);
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  function onPrintReview() {
    setPrinting(true);
  }

  function setOverviewRef(ref: HTMLElement | null) {
    if (ref) {
      overviewRef.current = ref;
    }
  }

  function setReviewRef(ref: HTMLElement | null) {
    if (ref) {
      reviewRef.current = ref;
    }
  }

  const createCategoryMakeModel = async (e: ICSVEquipment) => {
    if (isNil(e.categoryId) && !isNil(e.category)) {
      const fetchedCategory = await getCategory(e.category);
      if (fetchedCategory) {
        e.categoryId = fetchedCategory.id;
      } else {
        const createInput: EquipmentCategoryCreateInput = {
          title: e.category,
          createdOn: DateTime.now().toISO(),
        };
        const newCategory = await createEquipmentCategory({ createInput, image: null });
        e.categoryId = newCategory.id;
      }
    }

    if (isNil(e.makeId) && !isNil(e.make)) {
      const fetchedMake = await getMake(e.make);
      if (fetchedMake) {
        e.makeId = fetchedMake.id;
      } else {
        const createInput: EquipmentMakeCreateInput = {
          title: e.make,
          createdOn: DateTime.now().toISO(),
        };
        const newMake = await createEquipmentMake(createInput);
        e.makeId = newMake.id;
      }
    }

    if (isNil(e.modelId) && !isNil(e.model) && !isNil(e.categoryId) && !isNil(e.makeId) && !isNil(e.type)) {
      const fetchedModel = await getModel(e.model, e.makeId, e.categoryId, e.type);
      if (fetchedModel) {
        e.modelId = fetchedModel.id;
      } else {
        const input: EquipmentModelCreateInput = {
          createdOn: isoTimeStampUtc(),
          equipmentCategoryId: e.categoryId,
          equipmentMakeId: e.makeId,
          title: e.model,
          type: StringToEquipmentModelType(e.type.trim().toLowerCase()),
        };
        const newModel = await createEquipmentModel(input);
        e.modelId = newModel.id;
      }
    }
    return e;
  };

  const createEquipment = async (e: ICSVEquipment) => {
    let equip = cloneDeep(e);
    equip = await createCategoryMakeModel(e);
    const object = convertCsvEquipmentToEquipment(equip, organization.id!);
    try {
      const equipmentResponse = await client.mutate<{ createEquipment: IEquipment }>({
        mutation: CREATE_EQUIPMENT,
        variables: {
          equipment: { ...object, createdOn: isoTimeStampUtc() },
        },
      });

      let newEquipment = cloneDeep(equipmentResponse.data?.createEquipment);

      if (newEquipment && object.trackManualHours && equip.hourMeter && isNumberString(equip.hourMeter)) {
        const hourResponse = await createEquipmentHours(object.id, toNumber(equip.hourMeter));
        newEquipment = { ...newEquipment, lastHours: hourResponse.data?.createEquipmentHours };
      }

      if (newEquipment && equip.hourlyRate && isNumberString(equip.hourlyRate)) {
        const costHistoryResponse = await createEquipmentCostHistory(
          object.id,
          undefined,
          toNumber(equip.hourlyRate),
          costHistoryEffectiveDate
        );
        newEquipment = { ...newEquipment, costHistory: [costHistoryResponse] };
      }

      return {
        previousEquipment: equip,
        mutatedEquipment: { ...equipmentResponse, data: { createEquipment: newEquipment } },
      };
    } catch (error) {
      return {
        previousEquipment: equip,
        mutatedEquipment: { data: { createEquipment: undefined } },
      };
    }
  };

  const updateEquipment = async (e: ICSVEquipment) => {
    let equip = cloneDeep(e);
    equip = await createCategoryMakeModel(e);
    const object = convertCsvEquipmentToUpdateEquipment(equip);

    const equipmentResponse = await client.mutate<{ updateEquipment: IEquipment }>({
      mutation: UPDATE_EQUIPMENT,
      variables: { equipment: object },
    });

    let updatedEquipment = cloneDeep(equipmentResponse.data?.updateEquipment);

    if (updatedEquipment && object.trackManualHours && equip.hourMeter && isNumberString(equip.hourMeter)) {
      const newHourMeterValue = toNumber(equip.hourMeter);
      if (newHourMeterValue !== updatedEquipment.lastHours?.runningHours) {
        const hourResponse = await createEquipmentHours(object.id, toNumber(equip.hourMeter));
        updatedEquipment = { ...updatedEquipment, lastHours: hourResponse.data?.createEquipmentHours };
      }
    }

    if (updatedEquipment && equip.hourlyRate && isNumberString(equip.hourlyRate)) {
      const newCostHistoryValue = toNumber(equip.hourlyRate);

      if (
        newCostHistoryValue !== getCurrentEquipmentCostHistory(updatedEquipment.costHistory ?? [])?.operatorCostRate
      ) {
        const costHistoryResponse = await createEquipmentCostHistory(
          object.id,
          undefined,
          toNumber(equip.hourlyRate),
          costHistoryEffectiveDate
        );
        updatedEquipment = { ...updatedEquipment, costHistory: [costHistoryResponse] };
      }
    }

    return {
      previousEquipment: equip,
      mutatedEquipment: { ...equipmentResponse, data: { updateEquipment: updatedEquipment } },
    };
  };

  async function onSubmit() {
    openLoaderDetailsWithMessage(t('Saving...'));

    const reimporting = first(equipment)?.reimport;
    const imports = reimporting ? await onReimportSubmit() : await onImportSubmit();

    equipmentOverviewDialogDetails.close();

    // Only show overview on import
    if (!reimporting) {
      // Wait for dialog refs
      setTimeout(() => {
        setImportedEquipment(imports);
      }, 0);

      equipmentReviewDialogDetails.open();
    }

    onImportComplete();
    loaderDetails.close();
  }

  async function onReimportSubmit() {
    const results = [];

    for (const eq of equipment) {
      const result = await updateEquipment(eq);
      results.push(result);
    }

    return results.map<IImportedEquipment>((result) => {
      return {
        name: result.mutatedEquipment.data?.updateEquipment?.equipmentName ?? '',
        category: result.mutatedEquipment.data?.updateEquipment?.model.category.title ?? '',
        make: result.mutatedEquipment.data?.updateEquipment?.model.make.title ?? '',
        model: result.mutatedEquipment.data?.updateEquipment?.model.title ?? '',
        year: result.mutatedEquipment.data?.updateEquipment?.year
          ? result.mutatedEquipment.data?.updateEquipment?.year.toString()
          : '',
        hourMeter: result.mutatedEquipment.data?.updateEquipment?.lastHours?.runningHours.toFixed(1) ?? '',
        modelId: result.mutatedEquipment.data?.updateEquipment?.equipmentModelId ?? '',
        trackHourMeter: result.mutatedEquipment.data?.updateEquipment?.trackManualHours ?? false,
        success: true,
        hourlyRate:
          (
            getCurrentEquipmentCostHistory(result.mutatedEquipment.data?.updateEquipment?.costHistory ?? [])
              ?.operatorCostRate ?? 0
          ).toFixed(2) ?? '',
        type: result.mutatedEquipment.data.updateEquipment?.model.type ?? '',
        customField1: result.mutatedEquipment.data.updateEquipment?.customField1,
        customField2: result.mutatedEquipment.data.updateEquipment?.customField2,
        customField3: result.mutatedEquipment.data.updateEquipment?.customField3,
        customField4: result.mutatedEquipment.data.updateEquipment?.customField4,
        customField5: result.mutatedEquipment.data.updateEquipment?.customField5,
      };
    });
  }

  async function onImportSubmit() {
    const results = [];

    for (const eq of equipment) {
      const result = await createEquipment(eq);
      results.push(result);
    }

    return results.map<IImportedEquipment>((result) => {
      if (
        (result.mutatedEquipment.errors && result.mutatedEquipment.errors.length) ||
        !result.mutatedEquipment.data ||
        !result.mutatedEquipment.data.createEquipment
      ) {
        return {
          name: result.previousEquipment.name,
          category: result.previousEquipment.category ?? '',
          make: result.previousEquipment.model ?? '',
          model: result.previousEquipment.make ?? '',
          year: result.previousEquipment.year ?? '',
          hourMeter: result.previousEquipment.hourMeter ?? '',
          modelId: result.previousEquipment.modelId ?? '',
          trackHourMeter: !isNil(result.previousEquipment.hourMeter),
          success: false,
          hourlyRate: result.previousEquipment.hourlyRate ?? '',
          type: result.previousEquipment.type ?? '',
          customField1: result.previousEquipment.customField1,
          customField2: result.previousEquipment.customField2,
          customField3: result.previousEquipment.customField3,
          customField4: result.previousEquipment.customField4,
          customField5: result.previousEquipment.customField5,
        };
      } else {
        return {
          name: result.mutatedEquipment.data.createEquipment.equipmentName,
          category: result.mutatedEquipment.data?.createEquipment.model.category.title ?? '',
          make: result.mutatedEquipment.data?.createEquipment.model.make.title ?? '',
          model: result.mutatedEquipment.data?.createEquipment.model.title ?? '',
          year: result.mutatedEquipment.data?.createEquipment.year
            ? result.mutatedEquipment.data?.createEquipment.year.toString()
            : '',
          hourMeter: result.mutatedEquipment.data?.createEquipment.lastHours?.runningHours.toFixed(1) ?? '',
          modelId: result.mutatedEquipment.data?.createEquipment.equipmentModelId ?? '',
          trackHourMeter: result.mutatedEquipment.data?.createEquipment.trackManualHours ?? false,
          success: true,
          hourlyRate:
            (
              getCurrentEquipmentCostHistory(result.mutatedEquipment.data?.createEquipment.costHistory ?? [])
                ?.operatorCostRate ?? 0
            ).toFixed(2) ?? '',
          type: result.mutatedEquipment.data.createEquipment.model.type ?? '',
          customField1: result.mutatedEquipment.data.createEquipment.customField1,
          customField2: result.mutatedEquipment.data.createEquipment.customField2,
          customField3: result.mutatedEquipment.data.createEquipment.customField3,
          customField4: result.mutatedEquipment.data.createEquipment.customField4,
          customField5: result.mutatedEquipment.data.createEquipment.customField5,
        };
      }
    });
  }

  async function onDeleteEquipment(e: ICSVEquipment) {
    setEquipment(equipment.filter((obj) => obj.id !== e.id));
  }

  const reimporting = first(equipment)?.reimport;
  const classes = classNames('csv-import-equipment-container', className);

  return (
    <>
      <CsvImportDialog
        className={classes}
        isOpen={importDialogOpen}
        onClose={closeImportDialog}
        title={t('Import Equipment')}
        onFileSelected={parseCsv(false)}
        requiredFieldText={t('Name, type, category, make, and model are required fields.')}
        bodyText={t('Upload a .csv file of all equipment you would like to import.')}
        headers={CSVHeaders}
        sampleFileName={`${brand}-${'equipment-csv-headers'} -${dateUtils.isoTimeStampLocal()}.csv`}
        errors={getErrorMessages(errors, equipmentImportErrorMap)}
        prevalidationErrors={prevalidationErrors}
      />
      <Dialog isOpen={reimportDialogOpen} onClose={closeImportDialog}>
        <ReimportContent
          className="my-8 mx-6"
          prevalidationErrors={prevalidationErrors}
          errors={getErrorMessages(errors, equipmentImportErrorMap)}
          identifier="equipment"
          onDownloadList={onDownloadList}
          onUploadList={parseCsv(true)}
        />
      </Dialog>
      <CsvImportOverview
        dialogTitle={t('Upload Equipment')}
        onSubmit={onSubmit}
        submitText={isReimport ? t('Update All') : t('Create All')}
        isOpen={equipmentOverviewDialogDetails.isOpen}
        onClose={equipmentOverviewDialogDetails.close}
        size={'full'}
        dialogRef={setOverviewRef}
        confirmationPayload={{
          message: reimporting
            ? `${equipment.length} ${t('equipment will be updated. This cannot be undone.')}`
            : `${equipment.length} ${t('equipment will be created. This cannot be undone.')}`,
          submissionText: reimporting ? t('Update') : t('Create'),
          title: reimporting ? t('Update?') : t('Create'),
        }}
      >
        <div className="mx-5 my-6">
          <Label>{t('Hourly Rate Effective Date')}</Label>
          <div className="mb-2">
            {t(
              'The date you would like the rate change to take effect. Setting the date to the past will change past job costing data.'
            )}
          </div>
          <div className="my-6" style={{ width: '300px' }}>
            <DatePicker
              className={'hourly-cost-effective-date'}
              isOpen={datePickerOpenable.isOpen}
              onClose={datePickerOpenable.close}
              onOpen={datePickerOpenable.open}
              position={Position.BOTTOM_END}
              value={DateTime.fromISO(costHistoryEffectiveDate).toJSDate()}
              onChange={(datePickerValue) => {
                if (datePickerValue.value) {
                  setCostHistoryEffectiveDate(DateTime.fromJSDate(datePickerValue.value).startOf('day').toISO());
                }
              }}
            />
          </div>
          <CSVImportEquipmentOverviewTable
            equipment={equipment}
            loading={equipment.length === 0}
            lazyScrollTrigger={overviewRef.current}
            onDelete={onDeleteEquipment}
            orgCustomFields={organizationCustomFields}
          />
        </div>
      </CsvImportOverview>
      <CsvImportReview
        isOpen={equipmentReviewDialogDetails.isOpen}
        onClose={equipmentReviewDialogDetails.close}
        size={'full'}
        dialogTitle={t('Uploaded Equipment')}
        onPrint={onPrintReview}
        dialogRef={setReviewRef}
      >
        <h2 className="print mb-6">{t('Imported Equipment')}</h2>
        <div className="mx-5 my-6 table-container">
          <CSVImportEquipmentReviewTable
            equipment={importedEquipment}
            loading={importedEquipment.length === 0}
            lazyScrollTrigger={reviewRef.current}
            lazyLoad={!printing && importedEquipment.length > 200}
            orgCustomFields={organizationCustomFields}
          />
        </div>
      </CsvImportReview>
      <Loader isOpen={loaderDetails.isOpen} label={loaderMessage} overlay={true} />
      <Toast isOpen={toastDetails.isOpen} theme={Theme.DANGER} onClose={toastDetails.close} timeout={3000}>
        {toastErrorMessage}
      </Toast>
    </>
  );
};

export default CSVImportEquipmentContainer;
